import { useEffect, useMemo, useRef, useState } from 'react'
import type { Area } from '../../domain/area'
import type { ActivityRecord, Milestone } from '../../domain/history'
import { historyAmountLabel } from '../../domain/history'
import type { RewardRedemption } from '../../domain/reward'
import { CheckCircleIcon, CloseIcon, CoinsIcon, CounterIcon, FlagIcon, GiftIcon, TimerIcon, TrophyIcon } from './Icons'
import { useI18n } from '../../i18n'

interface Props {
  areas: Area[]
  records: ActivityRecord[]
  milestones: Milestone[]
  redemptions: RewardRedemption[]
  open: boolean
  onClose: () => void
}

const dayStamp = (timestamp: number) => {
  const date = new Date(timestamp)
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
}

const dateLabel = (timestamp: number, locale: string, todayLabel: string, yesterdayLabel: string) => {
  const stamp = dayStamp(timestamp)
  const today = new Date()
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1).getTime()
  if (stamp === dayStamp(today.getTime())) return todayLabel
  if (stamp === yesterday) return yesterdayLabel
  return new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(timestamp).toLocaleUpperCase(locale)
}

const timeLabel = (timestamp: number, locale: string) => new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(timestamp)
const fullDateLabel = (timestamp: number, locale: string) => new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }).format(timestamp)
const elapsedLabel = (startedAt: number, completedAt: number, language: 'en' | 'es', unavailable: string, underMinute: string, elapsedWord: string) => {
  const elapsed = completedAt - startedAt
  if (!Number.isFinite(elapsed) || elapsed < 0) return unavailable
  const minutes = Math.floor(elapsed / 60_000)
  if (minutes < 1) return underMinute
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `${minutes} min ${elapsedWord}`
  if (rest === 0) return `${hours} h ${language === 'es' ? 'transcurridas' : elapsedWord}`
  return `${hours} h ${rest} min ${elapsedWord}`
}

export const HistorySheet = ({ areas, records, milestones, redemptions, open, onClose }: Props) => {
  const { language, locale, copy: { common, history } } = useI18n()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [tab, setTab] = useState<'activity' | 'milestones' | 'redemptions'>('activity')
  const [areaId, setAreaId] = useState<string | null>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  const areaOptions = useMemo(() => {
    const names = new Map<string, string>()
    areas.forEach((area) => names.set(area.id, area.name))
    records.forEach((record) => { if (!names.has(record.areaId)) names.set(record.areaId, record.areaName) })
    milestones.forEach((milestone) => { if (!names.has(milestone.areaId)) names.set(milestone.areaId, milestone.areaName) })
    return [...names].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, locale))
  }, [areas, records, milestones, locale])

  useEffect(() => {
    if (areaId && !areaOptions.some((area) => area.id === areaId)) setAreaId(null)
  }, [areaId, areaOptions])

  const filteredRecords = areaId ? records.filter((record) => record.areaId === areaId) : records
  const filteredMilestones = areaId ? milestones.filter((milestone) => milestone.areaId === areaId) : milestones
  const activityGroups = filteredRecords.reduce<Array<{ stamp: number; items: ActivityRecord[] }>>((result, record) => {
    const stamp = dayStamp(record.completedAt)
    const current = result.at(-1)
    if (current?.stamp === stamp) current.items.push(record)
    else result.push({ stamp, items: [record] })
    return result
  }, [])
  const redemptionGroups = redemptions.reduce<Array<{ stamp: number; items: RewardRedemption[] }>>((result, redemption) => {
    const stamp = dayStamp(redemption.redeemedAt)
    const current = result.at(-1)
    if (current?.stamp === stamp) current.items.push(redemption)
    else result.push({ stamp, items: [redemption] })
    return result
  }, [])
  const totals = filteredRecords.reduce((sum, record) => ({ xp: sum.xp + record.xp, coins: sum.coins + record.coins }), { xp: 0, coins: 0 })

  return (
    <dialog ref={dialogRef} className="sheet history-sheet" onCancel={(event) => { event.preventDefault(); onClose() }} onClose={onClose}>
      <div className="sheet__body history-sheet__body">
        <div className="sheet__handle" />
        <div className="sheet__heading">
          <div><p className="eyebrow">{history.eyebrow}</p><h2>{history.title}</h2><p>{history.intro}</p></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label={common.close}><CloseIcon /></button>
        </div>

        <div className="history-tabs" role="tablist" aria-label={history.type}>
          <button className={tab === 'activity' ? 'history-tab history-tab--selected' : 'history-tab'} type="button" role="tab" aria-selected={tab === 'activity'} onClick={() => setTab('activity')}>{history.activity} ({filteredRecords.length})</button>
          <button className={tab === 'milestones' ? 'history-tab history-tab--selected' : 'history-tab'} type="button" role="tab" aria-selected={tab === 'milestones'} onClick={() => setTab('milestones')}>{history.milestones} ({filteredMilestones.length})</button>
          <button className={tab === 'redemptions' ? 'history-tab history-tab--selected' : 'history-tab'} type="button" role="tab" aria-selected={tab === 'redemptions'} onClick={() => setTab('redemptions')}>{history.redemptions} ({redemptions.length})</button>
        </div>

        {tab !== 'redemptions' && <div className="history-filters" aria-label={history.filter}>
          <button className={areaId === null ? 'history-filter history-filter--selected' : 'history-filter'} type="button" aria-pressed={areaId === null} onClick={() => setAreaId(null)}>{common.all}</button>
          {areaOptions.map((area) => <button className={areaId === area.id ? 'history-filter history-filter--selected' : 'history-filter'} type="button" aria-pressed={areaId === area.id} key={area.id} onClick={() => setAreaId(area.id)}>{area.name}</button>)}
        </div>}

        {tab === 'activity' && (filteredRecords.length === 0 ? <div className="history-empty"><CheckCircleIcon /><strong>{history.emptyActivity}</strong><p>{history.emptyActivityHelp}</p></div> : <>
          <div className="history-totals"><span>+{totals.xp} XP</span><span><CoinsIcon />+{totals.coins}</span></div>
          <div className="history-groups">{activityGroups.map((group) => <section className="history-group" key={group.stamp}><p className="eyebrow">{dateLabel(group.stamp, locale, history.today, history.yesterday)}</p><div>{group.items.map((record) => {
            const ActionIcon = record.type === 'timer' ? TimerIcon : record.type === 'counter' ? CounterIcon : CheckCircleIcon
            return <article className="history-row" key={record.id}><span className="history-row__icon"><ActionIcon /></span><div className="history-row__content"><strong>{record.actionName}</strong><small>{record.areaName} · {historyAmountLabel(record, language)}</small><small className="history-row__trace">{record.startedAt === null ? `${history.ended} ${timeLabel(record.completedAt, locale)} · ${history.startUnavailable}` : `${history.started} ${timeLabel(record.startedAt, locale)} · ${history.finished} ${timeLabel(record.completedAt, locale)} · ${elapsedLabel(record.startedAt, record.completedAt, language, history.durationUnavailable, history.underMinute, history.elapsed)}`}</small></div><div className="history-row__reward"><strong>+{record.xp} XP</strong><small>+{record.coins} 🪙</small></div></article>
          })}</div></section>)}</div>
        </>)}

        {tab === 'milestones' && (filteredMilestones.length === 0 ? <div className="history-empty"><TrophyIcon /><strong>{history.emptyMilestones}</strong><p>{history.emptyMilestonesHelp}</p></div> : <div className="milestone-list">{filteredMilestones.map((milestone) => {
          const MarkIcon = milestone.kind === 'objective' ? FlagIcon : TrophyIcon
          return <article className="milestone-row" key={milestone.id}><span className="history-row__icon"><MarkIcon /></span><div className="history-row__content"><span className="eyebrow">{milestone.kind === 'objective' ? history.goalCompleted : history.uniqueAction}</span><strong>{milestone.title}</strong><small>{milestone.areaName} · {fullDateLabel(milestone.achievedAt, locale)} · {timeLabel(milestone.achievedAt, locale)}</small></div>{(milestone.xp > 0 || milestone.coins > 0) && <div className="history-row__reward"><strong>+{milestone.xp} XP</strong><small>+{milestone.coins} 🪙</small></div>}</article>
        })}</div>)}

        {tab === 'redemptions' && (redemptions.length === 0 ? <div className="history-empty"><GiftIcon /><strong>{history.emptyRedemptions}</strong><p>{history.emptyRedemptionsHelp}</p></div> : <div className="history-groups redemption-groups">{redemptionGroups.map((group) => <section className="history-group" key={group.stamp}><p className="eyebrow">{dateLabel(group.stamp, locale, history.today, history.yesterday)}</p><div>{group.items.map((redemption) => <article className="redemption-row" key={redemption.id}><span className="history-row__icon"><GiftIcon /></span><div className="history-row__content"><span className="eyebrow">{history.rewardRedeemed}</span><strong>{redemption.rewardName}</strong><small>{timeLabel(redemption.redeemedAt, locale)} · {history.balance}: {redemption.coinsAfter} {common.coins}</small></div><div className="history-row__reward history-row__reward--spent"><strong>−{redemption.cost} 🪙</strong></div></article>)}</div></section>)}</div>)}
      </div>
    </dialog>
  )
}
